import React from 'react';
import { Target, Calendar, TrendingUp, User, ChevronRight } from 'lucide-react';

const EmployeePerformanceCard = ({ 
  employee, 
  performanceData, 
  goals, 
  isSelected = false, 
  expanded = false,
  onClick 
}) => {
  const getPerformanceRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600 bg-green-100';
    if (rating >= 3.5) return 'text-blue-600 bg-blue-100';
    if (rating >= 2.5) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getGoalCompletionColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-blue-600';
    if (rate >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getReviewStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const completedGoals = goals?.filter(g => g?.progress >= 100)?.length || 0;
  const goalCompletionRate = goals?.length > 0 ? Math.round((completedGoals / goals?.length) * 100) : 0;

  const nextReviewDate = performanceData?.nextReviewDate 
    ? new Date(performanceData?.nextReviewDate)
    : null;

  const isReviewOverdue = nextReviewDate && nextReviewDate < new Date();
  const reviewStatus = isReviewOverdue ? 'overdue' : (performanceData?.reviewStatus || 'pending');

  return (
    <div 
      onClick={onClick}
      className={`p-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
        isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
      } ${expanded ? 'border border-gray-200 rounded-lg bg-white shadow-sm mb-4' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1">
          {/* Employee Avatar */}
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="h-5 w-5 text-blue-600" />
            </div>
          </div>

          {/* Employee Info */}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900 truncate">
              {employee?.firstName} {employee?.lastName}
            </h4>
            <p className="text-xs text-gray-500 truncate">
              {employee?.title || 'No title'}
            </p>
          </div>
        </div>

        {/* Performance Rating */}
        <div className="flex items-center space-x-2">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getPerformanceRatingColor(performanceData?.currentRating || 0)}`}>
            {performanceData?.currentRating || '0.0'}/5
          </div>
          {!expanded && <ChevronRight className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Performance Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <Target className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-medium text-gray-600">Goals Progress</span>
              </div>
              <div className="flex items-baseline space-x-1">
                <span className={`text-lg font-bold ${getGoalCompletionColor(goalCompletionRate)}`}>
                  {goalCompletionRate}%
                </span>
                <span className="text-xs text-gray-500">
                  ({completedGoals}/{goals?.length || 0})
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-medium text-gray-600">Review Status</span>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getReviewStatusColor(reviewStatus)}`}>
                {reviewStatus?.replace('_', ' ')?.toUpperCase() || 'PENDING'}
              </span>
            </div>
          </div>

          {/* Goals Overview */}
          {goals?.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-600 mb-2">Recent Goals</h5>
              <div className="space-y-2">
                {goals?.slice(0, 2)?.map((goal, index) => (
                  <div key={goal?.id || index} className="flex items-center justify-between">
                    <span className="text-xs text-gray-700 truncate flex-1">
                      {goal?.title}
                    </span>
                    <div className="flex items-center space-x-2 ml-2">
                      <div className="w-12 bg-gray-200 rounded-full h-1">
                        <div 
                          className="bg-blue-600 h-1 rounded-full" 
                          style={{ width: `${Math.min(goal?.progress || 0, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 w-8">
                        {Math.round(goal?.progress || 0)}%
                      </span>
                    </div>
                  </div>
                ))}
                {goals?.length > 2 && (
                  <p className="text-xs text-gray-500">+{goals?.length - 2} more goals</p>
                )}
              </div>
            </div>
          )}

          {/* Performance Trend */}
          <div className="flex items-center space-x-2 text-xs">
            <TrendingUp className="h-3 w-3 text-green-500" />
            <span className="text-gray-600">Performance trend: </span>
            <span className="text-green-600 font-medium">Improving</span>
          </div>

          {/* Next Review Date */}
          {nextReviewDate && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Next Review:</span>
              <span className={`font-medium ${isReviewOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                {nextReviewDate?.toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeePerformanceCard;